// apps/office/src/components/office/InteriorQuotationPricingDialog.jsx
import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Save, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import pb from '../../lib/pocketbaseClient';

const rs = (v) => 'Rs. ' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Number(v) || 0);

const pArr = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch (e) { return []; } }
  return [];
};

const computeBase = (quotation) => {
  const items = pArr(quotation && quotation.items);
  return items.reduce((sum, blk) => {
    const prods = pArr(blk.products || blk.items);
    return sum + prods.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  }, 0);
};

const InteriorQuotationPricingDialog = ({ open, onOpenChange, quotation, onSaved }) => {
  const queryClient = useQueryClient();

  const base = computeBase(quotation);

  const [profitMargin, setProfitMargin] = useState(20);
  const [cgstEnabled, setCgstEnabled] = useState(false);
  const [cgstRate, setCgstRate] = useState(9);
  const [sgstEnabled, setSgstEnabled] = useState(false);
  const [sgstRate, setSgstRate] = useState(9);
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(0);
  const [transportation, setTransportation] = useState(0);
  const [deepCleaning, setDeepCleaning] = useState(0);

  useEffect(() => {
    if (!quotation) return;
    setProfitMargin(Number(quotation.profitMargin) || 20);
    setCgstEnabled(!!quotation.cgstEnabled);
    setCgstRate(Number(quotation.cgstRate) || 9);
    setSgstEnabled(!!quotation.sgstEnabled);
    setSgstRate(Number(quotation.sgstRate) || 9);
    setDiscountType(quotation.discountType || 'percentage');
    setDiscountValue(Number(quotation.discountValue) || 0);
    setTransportation(Number(quotation.transportation) || 0);
    setDeepCleaning(Number(quotation.deepCleaning) || 0);
  }, [quotation, open]);

  // ── live calculation ──
  const profitAmt = base * (Number(profitMargin) || 0) / 100;
  const subtotal = base + profitAmt;
  const cgstAmt = cgstEnabled ? subtotal * (Number(cgstRate) || 0) / 100 : 0;
  const sgstAmt = sgstEnabled ? subtotal * (Number(sgstRate) || 0) / 100 : 0;
  const afterTax = subtotal + cgstAmt + sgstAmt;
  const transportAmt = Number(transportation) || 0;
  const cleaningAmt = Number(deepCleaning) || 0;
  const discAmt = discountType === 'percentage'
    ? afterTax * (Number(discountValue) || 0) / 100
    : (Number(discountValue) || 0);
  const finalTotal = afterTax - discAmt + transportAmt + cleaningAmt;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        profitMargin: Number(profitMargin) || 0,
        subtotal: subtotal,
        cgstEnabled,
        cgstRate: Number(cgstRate) || 0,
        cgstAmount: cgstAmt,
        sgstEnabled,
        sgstRate: Number(sgstRate) || 0,
        sgstAmount: sgstAmt,
        discountType,
        discountValue: Number(discountValue) || 0,
        discountAmount: discAmt,
        transportation: Number(transportation) || 0,
        deepCleaning: Number(deepCleaning) || 0,
        total: finalTotal,
      };
      return pb.collection('interior_quotations').update(quotation.id, payload);
    },
    onSuccess: () => {
      toast.success('✅ Pricing updated');
      queryClient.invalidateQueries(['interior-quotations']);
      if (quotation.projectId) queryClient.invalidateQueries(['interior-quotations', quotation.projectId]);
      queryClient.invalidateQueries(['interior-quotations-approved-all']);
      onOpenChange(false);
      if (onSaved) onSaved();
    },
    onError: (err) => {
      toast.error('Failed: ' + (err.message || 'Unknown error'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pricing &amp; Charges</DialogTitle>
          <DialogDescription>
            {quotation?.quoteNumber} — Configure profit, GST, discount &amp; extra charges
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Base */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
            <span className="text-sm text-slate-600 font-medium">Base Modules Total</span>
            <span className="font-bold text-slate-800">{rs(base)}</span>
          </div>

          {/* Profit Margin */}
          <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
            <Label>Profit Margin %</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="0.1"
                value={profitMargin}
                onChange={(e) => setProfitMargin(e.target.value)}
                className="w-28 text-right"
              />
              <span className="text-xs text-slate-500 w-24 text-right">+ {rs(profitAmt)}</span>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1 pb-1 border-t border-slate-200">
            <span className="text-sm font-semibold text-slate-700">Subtotal (with profit)</span>
            <span className="font-bold text-slate-800">{rs(subtotal)}</span>
          </div>

          {/* GST */}
          <div className="space-y-2 pt-2">
            <p className="text-xs uppercase text-slate-500 font-semibold tracking-wide">GST</p>

            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center">
              <input type="checkbox" checked={cgstEnabled} onChange={(e) => setCgstEnabled(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">CGST</span>
              <Input type="number" min="0" step="0.1" value={cgstRate} onChange={(e) => setCgstRate(e.target.value)} disabled={!cgstEnabled} className="w-20 text-right" />
              <span className="text-xs text-slate-500 w-24 text-right">{cgstEnabled ? '+ ' + rs(cgstAmt) : '—'}</span>
            </div>

            <div className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center">
              <input type="checkbox" checked={sgstEnabled} onChange={(e) => setSgstEnabled(e.target.checked)} className="w-4 h-4" />
              <span className="text-sm">SGST</span>
              <Input type="number" min="0" step="0.1" value={sgstRate} onChange={(e) => setSgstRate(e.target.value)} disabled={!sgstEnabled} className="w-20 text-right" />
              <span className="text-xs text-slate-500 w-24 text-right">{sgstEnabled ? '+ ' + rs(sgstAmt) : '—'}</span>
            </div>
          </div>

          {/* Discount */}
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center pt-2">
            <Label>Discount</Label>
            <Select value={discountType} onValueChange={setDiscountType}>
              <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">%</SelectItem>
                <SelectItem value="amount">Rs.</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" min="0" step="0.1" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="w-24 text-right" />
            <span className="text-xs text-slate-500 w-24 text-right">- {rs(discAmt)}</span>
          </div>

          {/* Extra charges */}
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
            <Label>Transportation</Label>
            <Input type="number" min="0" value={transportation} onChange={(e) => setTransportation(e.target.value)} className="w-28 text-right" />
            <span className="text-xs text-slate-500 w-24 text-right">+ {rs(transportation)}</span>
          </div>

          <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
            <Label>Deep Cleaning</Label>
            <Input type="number" min="0" value={deepCleaning} onChange={(e) => setDeepCleaning(e.target.value)} className="w-28 text-right" />
            <span className="text-xs text-slate-500 w-24 text-right">+ {rs(deepCleaning)}</span>
          </div>

          {/* Final */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-50 border border-emerald-200 mt-2">
            <span className="text-sm font-semibold text-emerald-800">FINAL TOTAL</span>
            <span className="font-bold text-emerald-700 text-base">{rs(finalTotal)}</span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saveMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InteriorQuotationPricingDialog;
